// Core lib dependencies.
import moment from 'moment';
import _isEqual from 'lodash/isEqual';

// Import action types.
import ActionTypes from './actionTypes.js';

// Selectors.
import Selectors from './selectors.js';

// Config.
import Config from './config/config.js';
import Enums from './config/enums.js';

// Model business logic.
import { Logic } from './model.js';

// Misc dependencices.
import GoalUtil from './utils/goal.js';
import MilestoneUtil from './utils/milestone.js';

/**
 * <pre>
 * Goal module Actions.
 *
 * Summary of responsibilities:
 *
 *  - implements Redux action creators specific to component
 *
 * FYI:
 *
 * We are using {@link https://github.com/gaearon/redux-thunk|Redux Thunk} middleware on our {@link https://github.com/reactjs/redux|Redux} store.
 * For this reason you will see that inside each action creator, we return a function, rather than an action object directly.
 * </pre>
 *
 * @see {@link https://github.com/gaearon/redux-thunk|Redux Thunk Documentation}
 * @see {@link https://github.com/reactjs/redux|Redux Documentation}
 *
 * @author Tim Loram
 *
 * @memberof module:Goal
 * @namespace
 *
 * @type {Object}
 */
const Actions = {
  /**
   * Register a component instance.
   *
   * @param id 				{string} The DOM id of the module.
   * @param initialConfig 	{object} Any initial config to add to the state.
   */
  registerInstance: function(id, initialConfig) {
    return function(dispatch) {
      dispatch({
        type:          ActionTypes.REGISTER_INSTANCE,
        id:            id,
        initialConfig: initialConfig,
      });
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Check Goal state.
   *
   * @param id				{string} The DOM id of the module.
   * @param metricId 			{string} The Metric Id we are checking.
   * @param strategies 		{object} Strategies.
   * @param dates 			{object} Goal start and end dates.
   * @param labels			{object} Labels.
   * @param goalType			{string} The Goal type identifier.
   * @param goalTypeArgs 		{object} Additional args specific to the Goal type.
   */
  checkState: function(id, metricId, strategies, dates, labels, goalType, goalTypeArgs) {
    return (dispatch, getState) => {
      // - - - - - - - - - - - - - - - - - -
      // Define recursion function.
      const recurse = () => {
        setTimeout(() => {
          Actions.checkState(...arguments)(dispatch, getState);
        }, Config.intervals.state);
      };

      // - - - - - - - - - - - - - - - - - -
      // Retrieve any updated values we need from the current state.
      const previousState = Selectors.getState(getState(), id);
      const progress = Selectors.getProgress(getState(), id);

      // Keep hold of new state.
      let state;

      // - - - - - - - - - - - - - - - - - -
      // Behave according to Goal type.
      switch (goalType) {
        // Just has an end date target (see how many participants we can get before the end date).
        case Enums.GOAL_TYPE_DATE:
          state = Logic.StateProcessing.dateGoal(id, metricId, strategies, dates, labels, progress);

          break;

        // Number target (no end date, can take as long as required to reach goal).
        case Enums.GOAL_TYPE_NUMBER:
          state = Logic.StateProcessing.numberGoal(
            id,
            metricId,
            strategies,
            dates,
            labels,
            goalTypeArgs.milestones,
            progress
          );

          break;

        // A number and a date target (see if we can reach the target before the end date).
        case Enums.GOAL_TYPE_DATENUMBER:
          state = Logic.StateProcessing.dateNumberGoal(
            id,
            metricId,
            strategies,
            dates,
            labels,
            goalTypeArgs.milestones,
            progress
          );

          break;
      }

      // - - - - - - - - - - - - - - - - - -
      // Dispatch state update action.
      Dispatch.updateState(dispatch, id, state, previousState);

      // - - - - - - - - - - - - - - - - - -
      // Recheck at interval defined in config.
      recurse();
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Check Goal progress.
   *
   * @param id 				{string} The DOM id of the module.
   * @param metricId 			{string} The Metric Id we are checking.
   * @param dates 			{object} Goal start and end dates.
   * @param targets 			{object} Goal numeric and date targets.
   * @param params 			{object} Params relevant to making the api endpoint call.
   * @param goalType 			{string} The Goal type identifier.
   * @param goalTypeArgs 		{object} Additional args specific to the Goal type.
   */
  checkProgress: function(id, metricId, dates, targets, params, goalType, goalTypeArgs) {
    return (dispatch, getState) => {
      // Only check progress if goal has started.
      if (GoalUtil.hasGoalStarted(dates.start)) {
        // - - - - - - - - - - - - - - - - - -
        // Fork separate action to continually check local (ie non async) progress.
        Actions.checkLocalProgress(...arguments)(dispatch, getState);

        // - - - - - - - - - - - - - - - - - -
        // Define recursion function.
        const recurse = () => {
          setTimeout(() => {
            Actions.checkProgress(...arguments)(dispatch, getState);
          }, Logic.ProgressProcessing.getDynamicInterval(getState, id));
        };

        // - - - - - - - - - - - - - - - - - -
        // Get global config service.
        const globalConfig = NGXD.getServiceContainer().getService('config');

        // - - - - - - - - - - - - - - - - - -
        // Build the endpoint url.
        let url = globalConfig.get('endpoints.metricSecure') + '/' + Config.api.progress;
        url = url.replace('{METRIC_ID}', metricId);

        // - - - - - - - - - - - - - - - - - -
        // Get the campaign ID.
        const cID = globalConfig.get('campaign.id');

        // - - - - - - - - - - - - - - - - - -
        // Get Ajax service.
        const ajaxService = NGXD.getServiceContainer().getService('ajax');

        // - - - - - - - - - - - - - - - - - -
        // Execute async API call to check goal progress.
        ajaxService
          .get(
            url,
            Object.assign(params, { cID })

            // When response received.
          )
          .then(response => {
            // Grab response values.
            const _respCode = response.status;
            const _respStatusText = response.statusText;
            const _respData = response.data;

            // Behave according to server response code.
            switch (_respCode) {
              // Everything ok.
              case 200:
                // Retrieve any updated values we need from the current state.
                const previousProgress = Selectors.getProgress(getState(), id);
                const showCaptcha = Selectors.getShowCaptcha(getState(), id);
                const showConfirmation = Selectors.getShowConfirmation(getState(), id);

                // Keep hold of new progress.
                let progress;

                // - - - - - - - - - - - - - - - - - -
                // Did we encounter an error with the data returned? Assume the worst :)
                let dataError = true;

                // Behave according to Goal type.
                switch (goalType) {
                  // - - - - - - - - - - - - - - - - - -
                  // Just has an end date target (see how many participants we can get before the end date).
                  case Enums.GOAL_TYPE_DATE:
                    // If value was retrieved successfully.
                    if (_respData.value !== null) {
                      // Process and build progress object.
                      progress = Logic.ProgressProcessing.buildDateProgress(dates, _respData, previousProgress);

                      dataError = false;
                    }

                    break;

                  // - - - - - - - - - - - - - - - - - -
                  // Number target (no end date, can take as long as required to reach goal).
                  case Enums.GOAL_TYPE_NUMBER:
                    // If percent was retrieved successfully.
                    if (_respData.percent !== null) {
                      // Process and build progress object.
                      progress = Logic.ProgressProcessing.buildNumberProgress(
                        goalTypeArgs.milestones,
                        _respData,
                        previousProgress,
                        targets
                      );

                      dataError = false;
                    }

                    break;

                  // - - - - - - - - - - - - - - - - - -
                  // A number and a date target (see if we can reach the target before the end date).
                  case Enums.GOAL_TYPE_DATENUMBER:
                    // If milestones were retrieved successfully.
                    if (_respData.milestones !== null) {
                      // Process and build progress object.
                      progress = Logic.ProgressProcessing.buildDateNumberProgress(
                        goalTypeArgs.milestones,
                        _respData,
                        previousProgress,
                        targets
                      );

                      dataError = false;
                    }

                    break;
                }

                // If something went wrong.
                if (dataError) {
                  Dispatch.errorFromResponse(dispatch, id, _respData);

                  // Otherwise, if everything went ok.
                } else {
                  // Calculate new dynamic progress interval.
                  const previousIntervals = Selectors.getIntervals(getState(), id);
                  const intervals = Logic.ProgressProcessing.buildIntervalsObject();
                  intervals.progress = Logic.ProgressProcessing.calculateDynamicInterval(
                    previousIntervals.progress,
                    progress
                  );

                  // Dispatch update intervals action.
                  Dispatch.updateIntervals(dispatch, id, intervals, previousIntervals);

                  // Dispatch update progress action.
                  if (!showCaptcha && !showConfirmation) {
                    Dispatch.updateProgress(dispatch, id, progress, previousProgress);
                  }
                }

                break;
            }

            // If goal has not ended, recheck at interval defined in config.
            if (!GoalUtil.hasGoalEnded(dates.end)) {
              recurse();
            }

            // If error encountered.
          })
          .catch(error => {
            // Recheck at interval defined in config.
            recurse();
          });

        // If goal has not yet started, delay and reschedule another check for just after its start time.
      } else {
        const nowTimestamp = moment().valueOf();
        const goalStartDelay = dates.start - nowTimestamp + 500;

        setTimeout(() => {
          Actions.checkProgress(...arguments)(dispatch, getState);
        }, goalStartDelay);
      }
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Check Goal local (ie non async) progress.
   *
   * @param id 				{string} The DOM id of the module.
   * @param metricId 			{string} The Metric Id we are checking.
   * @param dates 			{object} Goal start and end dates.
   * @param targets 			{object} Goal numeric and date targets.
   * @param params 			{object} Params relevant to making the api endpoint call.
   * @param goalType 			{string} The Goal type identifier.
   * @param goalTypeArgs 		{object} Additional args specific to the Goal type.
   */
  checkLocalProgress: function(id, metricId, dates, targets, params, goalType, goalTypeArgs) {
    return (dispatch, getState) => {
      // - - - - - - - - - - - - - - - - - -
      // Define recursion function.
      const recurse = () => {
        setTimeout(() => {
          Actions.checkLocalProgress(...arguments)(dispatch, getState);
        }, Config.intervals.localProgress);
      };

      // - - - - - - - - - - - - - - - - - -
      // Retrieve any updated values we need from the current state.
      const previousProgress = Selectors.getProgress(getState(), id);
      const showCaptcha = Selectors.getShowCaptcha(getState(), id);
      const showConfirmation = Selectors.getShowConfirmation(getState(), id);

      // - - - - - - - - - - - - - - - - - -
      // Process and build progress object.
      const progress = Logic.ProgressProcessing.localProgress(id, metricId, dates, goalType, previousProgress);

      // - - - - - - - - - - - - - - - - - -
      // Dispatch update progress action.
      if (!showCaptcha && !showConfirmation) {
        if (progress) {
          Dispatch.updateProgress(dispatch, id, progress, previousProgress);
        }
      }

      // If goal has not ended, recheck at interval defined in config.
      if (!GoalUtil.hasGoalEnded(dates.end)) {
        recurse();
      }
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Participate in a Goal.
   *
   * @param id 				{string} The DOM id of the module.
   * @param metricId 			{string} The Metric Id we are participating with.
   * @param dates 			{object} Goal start and end dates.
   * @param targets 			{object} Goal numeric and date targets.
   * @param params 			{object} Params relevant to making the api endpoint call.
   * @param goalType 			{string} The Goal type identifier.
   * @param goalTypeArgs 		{object} Additional args specific to the Goal type.
   */
  participate: function(id, metricId, strategies, dates, targets, params, goalType, goalTypeArgs) {
    return function(dispatch, getState) {
      // Get global config service.
      const globalConfig = NGXD.getServiceContainer().getService('config');

      // - - - - - - - - - - - - - - - - - -
      // Build the endpoint url.
      let url = globalConfig.get('endpoints.metricSecure') + '/' + Config.api.participate;
      url = url.replace('{METRIC_ID}', metricId);

      // - - - - - - - - - - - - - - - - - -
      // Get Ajax service.
      const ajaxService = NGXD.getServiceContainer().getService('ajax');

      // - - - - - - - - - - - - - - - - - -
      // Get the campaign ID.
      const cID = globalConfig.get('campaign.id');

      // - - - - - - - - - - - - - - - - - -
      // Execute async API call to participate.
      ajaxService
        .post(
          url,
          Object.assign(params, { cID })

          // - - - - - - - - - - - - - - - - - -
          // When response received.
        )
        .then(response => {
          // Grab response values.
          const _respCode = response.status;
          const _respStatusText = response.statusText;
          const _respData = response.data;

          // Behave according to server response code.
          switch (_respCode) {
            // - - - - - - - - - - - - - - - - - -
            // Everything ok.
            case 200:
              // If we successfully recorded.
              if (_respData.recorded) {
                // Retrieve any updated values we need from the current state.
                const previousProgress = Selectors.getProgress(getState(), id);
                let progress;

                // Behave according to Goal type.
                switch (goalType) {
                  // - - - - - - - - - - - - - - - - - -
                  // Just has an end date target (see how many participants we can get before the end date).
                  case Enums.GOAL_TYPE_DATE:
                    // Record the fact that this user has already participated for this Metric.
                    GoalUtil.storeUserGoalParticipation(metricId);

                    // Process and build progress object.
                    progress = Logic.ProgressProcessing.buildDateProgress(dates, _respData.data, previousProgress);

                    break;

                  // - - - - - - - - - - - - - - - - - -
                  // Number target (no end date, can take as long as required to reach goal).
                  case Enums.GOAL_TYPE_NUMBER:
                    // Record the fact that this user has already participated for this Metric.
                    GoalUtil.storeUserGoalParticipation(metricId);

                    // Process and build progress object.
                    progress = Logic.ProgressProcessing.buildNumberProgress(
                      goalTypeArgs.milestones,
                      _respData.data,
                      previousProgress,
                      targets
                    );

                    break;

                  // - - - - - - - - - - - - - - - - - -
                  // A number and a date target (see if we can reach the target before the end date).
                  case Enums.GOAL_TYPE_DATENUMBER:
                    // Switch logic based on milestone strategy.
                    switch (strategies.milestone) {
                      // No milestones/Simple milestones.
                      case Enums.GOAL_MILESTONE_STRATEGY_TARGET:
                      case Enums.GOAL_MILESTONE_STRATEGY_MILESTONE:
                        // Record the fact that this user has already participated for this Metric.
                        GoalUtil.storeUserGoalParticipation(metricId);

                        break;

                      // Gated milestones.
                      case Enums.GOAL_MILESTONE_STRATEGY_GATEDMILESTONE:
                        // Record the fact that this user has already participated for this Metric+Milestone.
                        const activeMilestone = MilestoneUtil.getActiveMilestone(goalTypeArgs.milestones);
                        MilestoneUtil.storeUserMilestoneParticipation(metricId, activeMilestone);

                        break;
                    }

                    // Process and build progress object.
                    progress = Logic.ProgressProcessing.buildDateNumberProgress(
                      goalTypeArgs.milestones,
                      _respData.data,
                      previousProgress,
                      targets
                    );

                    break;
                }

                // Dispatch action to update progress.
                Dispatch.updateProgress(dispatch, id, progress, previousProgress);

                // Dispatch action to show confirmation mesage.
                dispatch({
                  type: ActionTypes.SHOW_CONFIRMATION,
                  id:   id,
                });

                // If something went wrong.
              } else {
                Dispatch.errorFromResponse(dispatch, id, _respData);
              }

              break;

            // - - - - - - - - - - - - - - - - - -
            // Suspicious.
            case 400:
              // Show dynamic captcha if required.
              if (strategies.fraud === Enums.GOAL_FRAUD_STRATEGY_DYNCAPTCHA) {
                // Dispatch action to show captcha.
                Actions.showCaptcha(id)(dispatch);
              }

              break;
          }

          // If error encountered.
        })
        .catch(function(error) {});
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Show captcha.
   *
   * @param id 				{string} The DOM id of the module.
   */
  showCaptcha: function(id) {
    return function(dispatch) {
      dispatch({
        type: ActionTypes.SHOW_CAPTCHA,
        id:   id,
      });
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Hide captcha.
   *
   * @param id 				{string} The DOM id of the module.
   */
  hideCaptcha: function(id) {
    return function(dispatch) {
      dispatch({
        type: ActionTypes.HIDE_CAPTCHA,
        id:   id,
      });
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Hide confirmation.
   *
   * @param id 				{string} The DOM id of the module.
   */
  hideConfirmation: function(id) {
    return function(dispatch) {
      dispatch({
        type: ActionTypes.HIDE_CONFIRMATION,
        id:   id,
      });
    };
  },

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /**
   * Hide error.
   *
   * @param id 				{string} The DOM id of the module.
   */
  hideError: function(id) {
    return function(dispatch) {
      dispatch({
        type: ActionTypes.HIDE_ERROR,
        id:   id,
      });
    };
  },
};

export default Actions;

/* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
/**
 * Centralized place for dispatching.
 *
 * @private
 */
const Dispatch = {
  /**
   * Dispatches an error action for a response.
   */
  errorFromResponse: function(dispatch, id, responseData) {
    // Grab supplied error message.
    const message = responseData.message;
    const label = responseData.messageId;

    // Dispatch action to store/show error mesage.
    dispatch({
      type:      ActionTypes.SHOW_ERROR,
      id:        id,
      message:   message,
      messageId: label,
    });
  },

  /**
   * Dispatches update progress action.
   */
  updateProgress: function(dispatch, id, progress, previousProgress) {
    // Dispatch update.
    Dispatch.selectiveDispatch(dispatch, progress, previousProgress, {
      type:     ActionTypes.UPDATE_PROGRESS,
      id:       id,
      progress: progress,
    });
  },

  /**
   * Dispatches update state action.
   */
  updateState: function(dispatch, id, state, previousState) {
    // Dispatch update.
    Dispatch.selectiveDispatch(dispatch, state, previousState, {
      type:  ActionTypes.UPDATE_STATE,
      id:    id,
      state: state,
    });
  },

  /**
   * Dispatches update intervals action.
   */
  updateIntervals: function(dispatch, id, intervals, previousIntervals) {
    // Dispatch update.
    Dispatch.selectiveDispatch(dispatch, intervals, previousIntervals, {
      type:      ActionTypes.UPDATE_INTERVALS,
      id:        id,
      intervals: intervals,
    });
  },

  /**
   * Compare action objects and only dispatch action if they differ.
   */
  selectiveDispatch: function(dispatch, current, previous, action) {
    // Compare objects and only dispatch action if they differ.
    if (!_isEqual(current, previous)) {
      // Dispatch action to update state.
      dispatch(action);
    }
  },
};
